// Compteur Airscale du radar (04/11/2026) : ce que le radar a consommé chez Airscale dans le mois, par opération. Le
// solde relevé la nuit est celui de tout le compte Airscale, qui sert aussi à d'autres outils. Bloc de l'écran
// « Abonnés », donc du seul gestionnaire ; la base refuse aussi `manager_airscale` à tout autre compte.
import { useQuery } from "@tanstack/react-query";
import { useTranslate } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { AccountCurrency } from "./accountCurrency";
import {
  formatCost,
  formatCredits,
  formatUsd,
  type AirscaleUsage,
} from "./subscribers";

const KNOWN_OPERATIONS = ["email", "profile", "mobile", "team"];

export const AirscaleCounter = ({
  month,
  currency,
  rate,
  locale,
}: {
  month: string;
  currency: AccountCurrency;
  rate: number | null;
  locale: string;
}) => {
  const translate = useTranslate();
  const t = (key: string, options?: Record<string, unknown>) =>
    translate(
      `resources.projects.subscribers.airscale_counter.${key}`,
      options,
    );
  const { data, isError } = useQuery({
    queryKey: ["manager_airscale", month],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient().rpc(
        "manager_airscale",
        { month },
      );
      if (error) throw error;
      return data as AirscaleUsage;
    },
    retry: false,
  });

  if (isError)
    return (
      <Card>
        <CardContent className="py-3 text-sm text-destructive">
          {t("error")}
        </CardContent>
      </Card>
    );
  if (!data) return null;

  const cost = (usd: number) => (
    <span title={formatUsd(usd, locale)}>
      {formatCost(usd, currency, rate, locale)}
    </span>
  );
  const operation = (name: string) =>
    KNOWN_OPERATIONS.includes(name) ? t(`operations.${name}`) : name;

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 py-3">
        <div>
          <div className="font-medium">{t("title")}</div>
          <div className="text-muted-foreground text-xs">{t("help")}</div>
        </div>
        <div className="flex flex-wrap gap-6 text-sm">
          <div>
            <div className="text-muted-foreground text-xs">{t("credits")}</div>
            <div className="text-lg font-semibold">
              {formatCredits(data.credits, locale)}
            </div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">{t("cost")}</div>
            <div className="text-lg font-semibold">{cost(data.cost_usd)}</div>
          </div>
          <div>
            <div className="text-muted-foreground text-xs">{t("calls")}</div>
            <div className="text-lg font-semibold">
              {t("calls_value", {
                found: data.found_calls,
                calls: data.calls,
              })}
            </div>
          </div>
        </div>
        {data.operations.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("none")}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("operation")}</TableHead>
                <TableHead className="text-right">{t("calls")}</TableHead>
                <TableHead className="text-right">{t("found")}</TableHead>
                <TableHead className="text-right">{t("credits")}</TableHead>
                <TableHead className="text-right">{t("cost")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.operations.map((row) => (
                <TableRow key={row.operation}>
                  <TableCell>{operation(row.operation)}</TableCell>
                  <TableCell className="text-right">{row.calls}</TableCell>
                  <TableCell className="text-right">
                    {row.found_calls}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCredits(row.credits, locale)}
                  </TableCell>
                  <TableCell className="text-right">
                    {cost(row.cost_usd)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
